import { FC } from "react";
import { useTranslation } from "react-i18next";

import {
  formatPassengerTooltipEntityWithId,
  formatPhoneLocal,
  getPassengerScopeRestrictions,
  getPassengerScopeRestrictionTooltipKeys,
  getPassengerTooltipStationTitleParts,
  getRidePassengerTooltipContent,
  hasPassengerScopeRestrictions,
  INACTIVE_EMOJI,
  IPassengerTooltipInfo,
  isPassengerRideSiteMismatch,
  PASSENGER_LIMITED_PERMISSIONS_EMOJI,
  PASSENGER_RIDE_SITE_MISMATCH_EMOJI,
  useHasPermissions,
  useProfileAuthorizationCheck,
} from "@ryde/shared";

import { RidePassengerTooltipWrapper } from "./ride-passenger-tooltip.styled";
import { getPassengerCardStationEmoji } from "./ride-passenger-tooltip.utils";

interface IRidePassengerTooltipContentProps {
  info: IPassengerTooltipInfo;
  currentStationId?: number;
  oppositeRideStationId?: number;
  rideSiteId?: number | null;
  rideSiteName?: string;
  companionLabel?: string;
}

export const RidePassengerTooltipContent: FC<
  IRidePassengerTooltipContentProps
> = ({
  info,
  currentStationId,
  oppositeRideStationId,
  rideSiteId,
  rideSiteName,
  companionLabel,
}) => {
  const { t } = useTranslation();
  const { hasPermissions: hasReadSitePermission } = useHasPermissions({
    entity: "site",
    action: "read",
  });
  const { hasPermissions: hasReadUnitPermission } = useHasPermissions({
    entity: "unit",
    action: "read",
  });
  const { hasPermissions: hasReadPassengerTypePermission } = useHasPermissions({
    entity: "passengerType",
    action: "read",
  });
  const { profileAuthorizationInCheck } = useProfileAuthorizationCheck();
  const passengerScopeRestrictions = getPassengerScopeRestrictions(
    info,
    profileAuthorizationInCheck,
  );
  const hasScopeRestrictions = hasPassengerScopeRestrictions(
    passengerScopeRestrictions,
  );

  const title = getRidePassengerTooltipContent(
    info.firstName,
    info.lastName,
    info.code,
    info.id,
    companionLabel,
  );

  const hasPassengerRideSiteMismatch = isPassengerRideSiteMismatch({
    passengerSiteId: info.siteId,
    rideSiteId,
  });

  const siteLabel = hasReadSitePermission
    ? formatPassengerTooltipEntityWithId({
        name: info.siteName,
        id: info.siteId,
      })
    : undefined;

  const unitLabel = hasReadUnitPermission
    ? formatPassengerTooltipEntityWithId({
        name: info.unitName,
        id: info.unitId,
      })
    : undefined;

  const rideSiteTooltipLabel = formatPassengerTooltipEntityWithId({
    name: rideSiteName,
    id: rideSiteId,
  });

  const passengerTypeLabel = hasReadPassengerTypePermission
    ? formatPassengerTooltipEntityWithId({
        name: info.passengerTypeName,
        id: info.passengerTypeId,
      })
    : undefined;

  return (
    <RidePassengerTooltipWrapper>
      <ul className="ride-passenger-tooltip__list">
        <li className="ride-passenger-tooltip__row ride-passenger-tooltip__row--title">
          {title}
        </li>
        {siteLabel && (
          <li className="ride-passenger-tooltip__row">
            {t("passenger.formLabel.siteId")}: {siteLabel}
            {hasPassengerRideSiteMismatch && rideSiteTooltipLabel && (
              <>
                {" "}
                {PASSENGER_RIDE_SITE_MISMATCH_EMOJI} (
                {t("ride.passengerTooltip.rideSite")}: {rideSiteTooltipLabel})
              </>
            )}
          </li>
        )}
        {unitLabel && (
          <li className="ride-passenger-tooltip__row">
            {t("passenger.formLabel.unitId")}: {unitLabel}
          </li>
        )}
        {passengerTypeLabel && (
          <li className="ride-passenger-tooltip__row">
            {t("passenger.formLabel.passengerTypeId")}: {passengerTypeLabel}
          </li>
        )}
        {info.phoneMobile && (
          <li className="ride-passenger-tooltip__row">
            {t("passenger.formLabel.phoneMobile")}:{" "}
            <bdi>{formatPhoneLocal(info.phoneMobile)}</bdi>
          </li>
        )}
        {info.phoneAlt && (
          <li className="ride-passenger-tooltip__row">
            {t("passenger.formLabel.phoneAlt")}:{" "}
            <bdi>{formatPhoneLocal(info.phoneAlt)}</bdi>
          </li>
        )}
        {hasScopeRestrictions && (
          <li className="ride-passenger-tooltip__row">
            {PASSENGER_LIMITED_PERMISSIONS_EMOJI}{" "}
            {t("ride.passengerTooltip.limitedPermissions")}
            {getPassengerScopeRestrictionTooltipKeys(passengerScopeRestrictions)
              .map(key => ` — ${t(key)}`)
              .join("")}
          </li>
        )}
        {info.isActive === false && (
          <li className="ride-passenger-tooltip__row">
            {INACTIVE_EMOJI}{" "}
            {t("general.inactive", { entity: t("passenger.single") })}
          </li>
        )}
        {info.stations.map(station => {
          const label = t(`passenger.formLabel.${station.fieldKey}`);
          const stationEmoji = getPassengerCardStationEmoji({
            stationId: station.stationId,
            currentStationId,
            oppositeRideStationId,
          });
          const { title: stationTitle, areaName } =
            getPassengerTooltipStationTitleParts({
              title: station.title,
              areaName: station.areaName,
            });

          return (
            <li
              className="ride-passenger-tooltip__row"
              key={`${station.fieldKey}-${station.stationId}`}
            >
              {label}: {stationTitle}
              {areaName || stationEmoji ? (
                <span
                  className="ride-passenger-tooltip__station-suffix"
                  dir={areaName ? "ltr" : undefined}
                >
                  {areaName ? ` (${areaName})` : null}
                  {stationEmoji ? ` ${stationEmoji}` : null}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </RidePassengerTooltipWrapper>
  );
};
